import React from 'react'
import PropTypes from 'prop-types'
import { Stack } from '@mui/material'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import MosSmartDialog from '../../../../common/MosSmartDialog'
import createDialogTitle from '../../../../../../generic/utilities/dialogUtil'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import ActiveDeviceRow from '../../../../common/ActiveDeviceRow'
import { TARGET_STATUS_CODE } from '../../utils/jobStatus'

// What the operator is told and the button they press, per act. Only the text changes between the
// two: the machines listed below, and what Sun does on confirm (close their periods), are the same.
//
// FINISH: "wherever it is still active", not "in these machines": Sun re-reads the open periods on the
// second call, so the list below is what was true when it answered, not a contract.
// INTERRUPT: this machine is NOT running the job (otherwise Sun would not have asked), so pausing it
// means pausing it on the machines below -- and the operator must know that before confirming.
const COPY = {
  [TARGET_STATUS_CODE.FINISH]: {
    message: 'This job is still running on other machines. It will be interrupted wherever it is still active, and finished here.',
    acceptLabel: 'Interrupt and finish'
  },
  [TARGET_STATUS_CODE.INTERRUPT]: {
    message: 'This job is still running on the machines below. It will be interrupted on all of them.',
    acceptLabel: 'Interrupt'
  }
}

/**
 * Second step of finishing -- or interrupting -- a job that is still open on OTHER machines.
 *
 * Sun answers the updateStatus PUT with HTTP 200 `{ type: 'CONFIRMATION_REQUIRED', reason:
 * 'JOB_ACTIVE_IN_OTHER_DEVICES', activeDevices }` instead of closing anything; confirming here
 * resends the same PUT with `interruptInOtherDevices: true`, and Sun closes those periods in a single
 * transaction. Cancelling writes nothing at all.
 *
 * - FINISH: the job is finished here and interrupted on the machines listed.
 * - INTERRUPT: asked only when THIS machine is not running the job; the job is interrupted on the
 *   machines listed. It used to be a silent no-op: nothing was paused and the job stayed active.
 *
 * It lives in its own file, rather than as a third dialog inside `JobStatusChangeDialog`, because of
 * `useDateFormatter`: that hook reads `dateFormats` off the theme and the DEFAULT export of
 * `useSmartTranslation`, neither of which the four existing suites of the parent dialog provide.
 * Mounted conditionally from the parent, the hook only ever runs when this confirmation is actually
 * on screen, so those suites keep their baseline untouched.
 *
 * Each row names the machine and, when Sun sends them, the mode the job runs in there plus who
 * opened that period and when -- the three things an operator weighs before closing someone else's
 * work. Missing extras simply do not paint: no dash, no placeholder that could read as a value.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.devices - The machines the job is still active on, as Sun sent them
 * @param {string} [props.statusCode=TARGET_STATUS_CODE.FINISH] - The status change being confirmed;
 *        picks the message and the accept label. Anything but INTERRUPT keeps the finish copy.
 * @param {boolean} [props.loading=false] - Disables the buttons while a request is in flight
 * @param {Function} props.onAccept - Confirms: resend the change asking to interrupt there too
 * @param {Function} props.onCancel - Declines: nothing is changed, anywhere
 */
const OtherDevicesConfirmDialog = ({
  devices,
  statusCode = TARGET_STATUS_CODE.FINISH,
  loading = false,
  onAccept,
  onCancel
}) => {
  const { t_ } = useSmartTranslation()
  const copy = COPY[statusCode] || COPY[TARGET_STATUS_CODE.FINISH]

  return (
    <MosSmartDialog
      title={createDialogTitle({
        color: 'warning',
        title: t_('Job active in other machines'),
        icon: PauseCircleOutlineIcon
      })}
      setOpen
      acceptLabel={copy.acceptLabel}
      acceptCallback={onAccept}
      cancelCallback={onCancel}
      disableButtons={loading}
      message={t_(copy.message)}
    >
      <Stack spacing={1} sx={{ px: 3, pb: 1 }}>
        {/* ENMIENDA 12 (G3-17): la fila se extrajo a `common/ActiveDeviceRow.jsx` SIN cambiarle el
            comportamiento, para que el cierre EN LOTE enseñe exactamente lo mismo. Este diálogo no
            cambia por fuera. */}
        {devices.map((device) => <ActiveDeviceRow key={device.id} device={device} />)}
      </Stack>
    </MosSmartDialog>
  )
}

OtherDevicesConfirmDialog.propTypes = {
  devices: PropTypes.array.isRequired,
  statusCode: PropTypes.string,
  loading: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default OtherDevicesConfirmDialog
